import { EditorOnly } from '../components/EditorOnly';
import { Icon } from '@iconify/react';
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type {
  IntegrationStatus,
  TopvisorCatalog,
  TopvisorPositionsData,
  TopvisorReport,
  TopvisorReportData,
  TopvisorSerpData,
} from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { Box, Grid, Stack, Typography } from '../components/mui-system';
import { PageHeader } from '../components/PageHeader';
import {
  TopvisorCatalogPanel,
  type TopvisorCatalogSelection,
} from '../components/TopvisorCatalogPanel';
import { useWorkspace } from '../workspace-context';

export function ReportsPage() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace!.id;
  const [reports, setReports] = useState<TopvisorReport[]>([]);
  const [selected, setSelected] = useState<TopvisorReport | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationStatus | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalog, setCatalog] = useState<TopvisorCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogWarning, setCatalogWarning] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [reportType, setReportType] = useState<'positions' | 'serp'>('positions');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const refreshCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError('');
    setCatalogWarning('');
    try {
      setCatalog(await api.topvisorCatalog(workspaceId));
    } catch (caught) {
      setCatalogError(message(caught, 'Не удалось получить каталог Topvisor'));
    } finally {
      setCatalogLoading(false);
    }
  }, [workspaceId]);

  const load = useCallback(async () => {
    const [nextReports, nextIntegrations] = await Promise.all([
      api.topvisorReports(workspaceId),
      api.integrations(),
    ]);
    setReports(nextReports);
    setIntegrations(nextIntegrations);
    setCatalog(null);
  }, [workspaceId]);

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    load()
      .catch((caught) => setError(message(caught, 'Не удалось загрузить отчёты')))
      .finally(() => setLoading(false));
  }, [load]);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    setCatalogWarning('');
    const data = new FormData(event.currentTarget);
    try {
      const report = await api.createTopvisorReport(workspaceId, {
        title: String(data.get('title')),
        type: reportType,
        topvisorProjectId: Number(data.get('topvisorProjectId')),
        regionIndex: Number(data.get('regionIndex')),
        searchEngineLabel: String(data.get('searchEngineLabel')),
        regionLabel: String(data.get('regionLabel')),
        device: String(data.get('device')) as 'desktop' | 'tablet' | 'mobile',
        depth: Number(data.get('depth')),
        comparisonProjectIds: integerList(String(data.get('comparisonProjectIds'))),
        trackedHosts: stringList(String(data.get('trackedHosts'))),
      });
      setReports((current) => [report, ...current]);
      setSelected(report);
      setShowForm(false);
      setNotice('Профиль создан. Запустите первый импорт из Topvisor.');
    } catch (caught) {
      setError(message(caught, 'Не удалось создать профиль'));
    } finally {
      setBusy(false);
    }
  }

  async function importCatalog(items: TopvisorCatalogSelection[]) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await api.importTopvisorReports(workspaceId, items);
      setReports(result.reports);
      const changed = result.created + result.updated;
      const parts = [
        `${result.created} создано`,
        `${result.updated} обновлено`,
        `${result.unchanged} без изменений`,
      ];
      setNotice(
        `Каталог Topvisor синхронизирован: ${parts.join(', ')}.${
          result.skipped.length ? ` Пропущено: ${result.skipped.length}.` : ''
        }${changed ? ' Данные появятся после ручного или ежедневного импорта.' : ''}`,
      );
      if (result.skipped.length) {
        setCatalogWarning(result.skipped.map((item) => item.message).join(' '));
      }
    } catch (caught) {
      setError(message(caught, 'Не удалось создать профили из Topvisor'));
    } finally {
      setBusy(false);
    }
  }

  async function open(reportId: string) {
    setBusy(true);
    setError('');
    try {
      setSelected(await api.topvisorReport(workspaceId, reportId));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (caught) {
      setError(message(caught, 'Не удалось открыть отчёт'));
    } finally {
      setBusy(false);
    }
  }

  async function sync() {
    if (!selected) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const report = await api.syncTopvisorReport(workspaceId, selected.id);
      setSelected(report);
      setReports((current) => current.map((item) => (item.id === report.id ? report : item)));
      setNotice(`Загружены данные Topvisor за ${report.latest?.reportDate ?? 'последнюю дату'}.`);
    } catch (caught) {
      setError(message(caught, 'Topvisor не вернул отчёт'));
      const [refreshed] = await Promise.allSettled([api.topvisorReport(workspaceId, selected.id)]);
      if (refreshed.status === 'fulfilled') {
        setSelected(refreshed.value);
        setReports((current) =>
          current.map((item) => (item.id === selected.id ? refreshed.value : item)),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (
      !selected ||
      !window.confirm(
        `Отключить отчёт «${selected.title}»? Последний успешный снимок сохранится, ежедневный импорт остановится.`,
      )
    )
      return;
    setBusy(true);
    setError('');
    try {
      await api.archiveTopvisorReport(workspaceId, selected.id);
      setReports((current) => current.filter((item) => item.id !== selected.id));
      setSelected(null);
      setNotice('Отчёт отключён. Последний успешный снимок и статус последней попытки сохранены.');
    } catch (caught) {
      setError(message(caught, 'Не удалось убрать отчёт'));
    } finally {
      setBusy(false);
    }
  }

  const data = selected?.latest?.data ?? null;

  return (
    <Box>
      <PageHeader
        title={selected?.title ?? 'Ежедневные отчёты'}
        description={
          selected
            ? reportMeta(selected)
            : 'Позиции и снимки выдачи из Topvisor — в читаемом виде, с ежедневным обновлением.'
        }
        action={
          selected ? (
            <>
              <Button variant="outlined" onClick={() => setSelected(null)}>
                К списку
              </Button>
              <EditorOnly permissions="reports.run">
                <Button
                  variant="contained"
                  disabled={busy || !integrations?.topvisor}
                  onClick={() => void sync()}
                  startIcon={
                    busy ? <CircularProgress size={16} /> : <Icon icon="solar:refresh-linear" />
                  }
                >
                  Обновить
                </Button>
              </EditorOnly>
              <EditorOnly permissions="reports.delete">
                <Tooltip title="Отключить отчёт и ежедневный импорт">
                  <IconButton color="error" disabled={busy} onClick={() => void archive()}>
                    <Icon icon="solar:trash-bin-trash-linear" />
                  </IconButton>
                </Tooltip>
              </EditorOnly>
            </>
          ) : (
            <EditorOnly permissions="reports.create">
              <Button
                variant="outlined"
                startIcon={<Icon icon="solar:add-circle-linear" />}
                onClick={() => setShowForm((value) => !value)}
              >
                Добавить вручную
              </Button>
            </EditorOnly>
          )
        }
      />

      {!integrations?.topvisor && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Добавьте <code>TOPVISOR_USER_ID</code> и <code>TOPVISOR_API_KEY</code> на сервере. Профили
          можно настроить заранее, но импорт будет недоступен.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      {selected ? (
        <ReportDetail report={selected} data={data} />
      ) : (
        <>
          {integrations?.topvisor && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography fontWeight={800}>Проекты Topvisor</Typography>
                      <Typography fontSize={13} color="text.secondary">
                        Подключение проектов и регионов для ежедневных отчётов
                      </Typography>
                    </Box>
                    <EditorOnly permissions="reports.import">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCatalogOpen(true);
                          if (!catalog) void refreshCatalog();
                        }}
                      >
                        Настроить проекты
                      </Button>
                    </EditorOnly>
                  </Stack>
                </CardContent>
              </Card>
              <Dialog
                open={catalogOpen}
                onClose={() => setCatalogOpen(false)}
                fullWidth
                maxWidth="lg"
              >
                <DialogTitle>Проекты Topvisor</DialogTitle>
                <DialogContent>
                  <TopvisorCatalogPanel
                    catalog={catalog}
                    reports={reports}
                    loading={catalogLoading}
                    busy={busy}
                    error={catalogError}
                    warning={catalogWarning}
                    onRefresh={() => void refreshCatalog()}
                    onImport={(items) => void importCatalog(items)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setCatalogOpen(false)}>Готово</Button>
                </DialogActions>
              </Dialog>
            </>
          )}
          {showForm && (
            <ReportForm
              type={reportType}
              onTypeChange={setReportType}
              onCancel={() => setShowForm(false)}
              onSubmit={create}
              busy={busy}
            />
          )}
          {loading ? (
            <Box py={8} textAlign="center">
              <CircularProgress />
            </Box>
          ) : reports.length ? (
            <Grid container spacing={1.6}>
              {reports.map((report) => (
                <Grid key={report.id} size={{ xs: 12, md: 6, xl: 4 }}>
                  <ReportCard report={report} disabled={busy} onOpen={() => void open(report.id)} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <EmptyState
              icon={<Icon icon="solar:document-add-linear" />}
              title="Отчётов пока нет"
              description="Выберите проекты и регионы в каталоге Topvisor выше или добавьте профиль вручную."
              action={
                <EditorOnly permissions="reports.create">
                  <Button variant="outlined" onClick={() => setShowForm(true)}>
                    Добавить вручную
                  </Button>
                </EditorOnly>
              }
            />
          )}
        </>
      )}
    </Box>
  );
}

function ReportForm({
  type,
  onTypeChange,
  onCancel,
  onSubmit,
  busy,
}: {
  type: 'positions' | 'serp';
  onTypeChange(value: 'positions' | 'serp'): void;
  onCancel(): void;
  onSubmit(event: FormEvent<HTMLFormElement>): void;
  busy: boolean;
}) {
  return (
    <Card sx={{ mb: 2.2, borderColor: 'primary.main' }}>
      <CardContent component="form" onSubmit={onSubmit} sx={{ p: { xs: 2, md: 2.7 } }}>
        <Stack direction="row" gap={1.2} alignItems="center" mb={2.2}>
          <Icon icon="solar:settings-minimalistic-bold-duotone" width={26} />
          <Box>
            <Typography variant="h3">Профиль ежедневного отчёта</Typography>
            <Typography color="text.secondary" fontSize={13}>
              ID проекта и индекс региона видны в Topvisor и не являются секретами.
            </Typography>
          </Box>
        </Stack>
        <Grid container spacing={1.7}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              name="title"
              label="Название"
              placeholder="BRAND · Москва · ПК"
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              label="Тип отчёта"
              value={type}
              onChange={(event) => onTypeChange(event.target.value as 'positions' | 'serp')}
              fullWidth
            >
              <MenuItem value="positions">Позиции проекта</MenuItem>
              <MenuItem value="serp">Снимок выдачи</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              name="topvisorProjectId"
              type="number"
              label="Project ID"
              slotProps={{ htmlInput: { min: 1 } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              name="regionIndex"
              type="number"
              label="Region index"
              slotProps={{ htmlInput: { min: 0 } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              name="searchEngineLabel"
              label="Поисковик"
              defaultValue="Яндекс"
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField name="regionLabel" label="Регион" placeholder="Москва" required fullWidth />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField select name="device" label="Устройство" defaultValue="desktop" fullWidth>
              <MenuItem value="desktop">ПК</MenuItem>
              <MenuItem value="tablet">Планшет</MenuItem>
              <MenuItem value="mobile">Смартфон</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField select name="depth" label="Глубина" defaultValue="100" fullWidth>
              {[10, 20, 30, 50, 100, 200, 300, 500].map((depth) => (
                <MenuItem key={depth} value={depth}>
                  Top-{depth}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {type === 'positions' ? (
            <Grid size={{ xs: 12 }}>
              <TextField
                name="comparisonProjectIds"
                label="ID конкурентов / дополнительных проектов"
                placeholder="1024, 1025, 1026"
                helperText="Необязательно. Разделяйте запятыми."
                fullWidth
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 12 }}>
              <TextField
                name="trackedHosts"
                label="Собственные домены"
                placeholder="sample.domain.name, reserve.example"
                helperText="Именно эти домены и их поддомены будут подсвечены в выдаче."
                required
                fullWidth
              />
            </Grid>
          )}
        </Grid>
        <Stack direction="row" justifyContent="flex-end" gap={1} mt={2.2}>
          <Button onClick={onCancel}>Отмена</Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? 'Сохраняем…' : 'Создать профиль'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ReportCard({
  report,
  disabled,
  onOpen,
}: {
  report: TopvisorReport;
  disabled: boolean;
  onOpen(): void;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea disabled={disabled} onClick={onOpen} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2.2 }}>
          <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">
            <Box
              sx={{
                width: 43,
                height: 43,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor:
                  report.type === 'positions' ? 'rgba(74,126,187,.13)' : 'rgba(46,125,74,.13)',
                color: report.type === 'positions' ? 'primary.main' : 'success.main',
              }}
            >
              <Icon
                icon={
                  report.type === 'positions'
                    ? 'solar:chart-square-bold-duotone'
                    : 'solar:list-check-bold-duotone'
                }
                width={24}
              />
            </Box>
            <SnapshotChip report={report} />
          </Stack>
          <Typography variant="h3" mt={2}>
            {report.title}
          </Typography>
          <Typography color="text.secondary" fontSize={13} mt={0.55}>
            {reportMeta(report)}
          </Typography>
          <Typography fontSize={12} mt={2} color="text.secondary">
            {report.latest?.status === 'complete'
              ? `Данные за ${displayDate(report.latest.reportDate)}`
              : (report.lastAttempt?.error ??
                report.latest?.error ??
                'Первый импорт ещё не запускался')}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function SnapshotChip({ report }: { report: TopvisorReport }) {
  if (report.lastAttempt?.status === 'error') {
    return <Chip size="small" color="error" label="Ошибка обновления" />;
  }
  if (!report.latest) return <Chip size="small" label="Нет данных" />;
  if (report.latest.status === 'error') return <Chip size="small" color="error" label="Ошибка" />;
  const old = isOldSnapshot(report);
  return (
    <Chip
      size="small"
      color={old ? 'warning' : 'success'}
      label={old ? 'Старый срез' : 'Загружен'}
    />
  );
}

function ReportDetail({
  report,
  data,
}: {
  report: TopvisorReport;
  data: TopvisorReportData | null;
}) {
  if (report.latest?.status === 'error') {
    return (
      <>
        <AttemptStatus report={report} />
        <Alert severity="error">
          {report.latest.error ?? 'Неизвестная ошибка импорта Topvisor'}
        </Alert>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <AttemptStatus report={report} />
        <EmptyState
          icon={<Icon icon="solar:cloud-download-linear" />}
          title="Данных ещё нет"
          description="Запустите импорт. Раздел сохранит последнюю успешную дату проверки, которую вернёт Topvisor."
        />
      </>
    );
  }
  return (
    <>
      <AttemptStatus report={report} />
      {isOldSnapshot(report) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Topvisor вернул старый срез за {displayDate(data.date)}. «Обновить» загружает уже
          выполненные проверки, а не запускает новый съём позиций. Проверьте дату последнего съёма в
          Topvisor для выбранного региона и устройства.
        </Alert>
      )}
      <SummaryCards data={data} />
      <Card>
        <CardContent sx={{ px: 2.2, pt: 2.1, pb: 1.3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="h3">
                {data.type === 'positions'
                  ? 'Позиции по запросам'
                  : 'Вхождения собственных доменов'}
              </Typography>
              <Typography color="text.secondary" fontSize={12} mt={0.4}>
                Topvisor · {displayDate(data.date)} · импорт{' '}
                {displayTime(report.latest?.capturedAt)}
              </Typography>
            </Box>
            <Chip
              label={
                data.type === 'positions' ? `${data.targets.length} проектов` : `Top-${data.depth}`
              }
            />
          </Stack>
        </CardContent>
        {data.type === 'positions' ? <PositionsTable data={data} /> : <SerpTable data={data} />}
      </Card>
    </>
  );
}

function AttemptStatus({ report }: { report: TopvisorReport }) {
  const attempt = report.lastAttempt;
  if (!attempt) return null;
  if (attempt.status === 'error') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Последняя попытка импорта {displayTime(attempt.capturedAt)} завершилась ошибкой:{' '}
        {attempt.error}.
        {report.latest?.status === 'complete' &&
          ` Показан последний успешный срез за ${displayDate(report.latest.reportDate)}.`}
      </Alert>
    );
  }
  return (
    <Typography color="text.secondary" fontSize={12} mb={2}>
      Последняя успешная проверка: {displayTime(attempt.capturedAt)}
    </Typography>
  );
}

function SummaryCards({ data }: { data: TopvisorReportData }) {
  const cards = useMemo(() => {
    if (data.type === 'positions') {
      return [
        ['Запросов', data.summary.keywordCount, 'в текущем срезе'],
        ['В Top-3', data.summary.top3, 'позиций по всем доменам'],
        ['В Top-10', data.summary.top10, `из ${data.summary.found} найденных`],
        [
          'Средняя позиция',
          data.summary.averagePosition ?? '—',
          `${data.summary.missing} без позиции`,
        ],
      ];
    }
    return [
      ['Запросов', data.summary.keywordCount, 'в снимке выдачи'],
      ['Есть наши', data.summary.keywordsWithTrackedHosts, 'запросов с вхождением'],
      ['В Top-10', data.summary.top10, `${data.summary.trackedAppearances} вхождений всего`],
      ['Лучшая позиция', data.summary.bestPosition ?? '—', `${data.summary.top3} в Top-3`],
    ];
  }, [data]);
  return (
    <Grid container spacing={1.3} mb={1.6}>
      {cards.map(([label, value, hint]) => (
        <Grid key={String(label)} size={{ xs: 6, lg: 3 }}>
          <Card>
            <CardContent sx={{ p: 1.8 }}>
              <Typography
                color="text.secondary"
                fontSize={11}
                textTransform="uppercase"
                letterSpacing=".08em"
              >
                {label}
              </Typography>
              <Typography variant="h2" mt={0.45}>
                {value}
              </Typography>
              <Typography color="text.secondary" fontSize={11} mt={0.35}>
                {hint}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function PositionsTable({ data }: { data: TopvisorPositionsData }) {
  return (
    <TableContainer sx={{ maxHeight: '65vh' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 230 }}>Запрос</TableCell>
            {data.targets.map((target) => (
              <TableCell key={target.id} align="center" sx={{ minWidth: 120 }}>
                {target.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row, index) => (
            <TableRow key={`${row.query}-${index}`} hover>
              <TableCell>
                <Typography fontSize={12.5} fontWeight={650}>
                  {row.query}
                </Typography>
              </TableCell>
              {data.targets.map((target) => (
                <TableCell key={target.id} align="center">
                  <RankChip rank={row.positions[String(target.id)] ?? null} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SerpTable({ data }: { data: TopvisorSerpData }) {
  return (
    <TableContainer sx={{ maxHeight: '65vh' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Запрос</TableCell>
            <TableCell>Наши домены</TableCell>
            <TableCell>Начало выдачи</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row, index) => {
            const tracked = row.results.filter((item) => item.tracked);
            return (
              <TableRow key={`${row.query}-${index}`} hover>
                <TableCell sx={{ minWidth: 240 }}>
                  <Typography fontSize={12.5} fontWeight={650}>
                    {row.query}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Stack direction="row" gap={0.6} flexWrap="wrap">
                    {tracked.length ? (
                      tracked.map((item) => (
                        <Chip
                          key={`${item.domain}-${item.position}`}
                          size="small"
                          color={item.position <= 10 ? 'success' : 'default'}
                          label={`${item.domain} · ${item.position}`}
                        />
                      ))
                    ) : (
                      <Typography color="text.secondary" fontSize={12}>
                        нет в Top-{data.depth}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 320 }}>
                  <Typography color="text.secondary" fontSize={11.5}>
                    {row.results
                      .slice(0, 5)
                      .map((item) => `${item.position}. ${item.domain}`)
                      .join('  ·  ') || 'нет данных'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RankChip({ rank }: { rank: number | null }) {
  if (rank === null)
    return (
      <Typography color="text.disabled" fontSize={12}>
        —
      </Typography>
    );
  return (
    <Chip
      size="small"
      label={rank}
      color={rank <= 3 ? 'success' : rank <= 10 ? 'primary' : rank <= 30 ? 'warning' : 'default'}
    />
  );
}

function reportMeta(report: TopvisorReport): string {
  return `${report.searchEngineLabel} · ${report.regionLabel} · ${deviceLabel(report.device)} · Top-${report.depth}`;
}

function isOldSnapshot(report: TopvisorReport): boolean {
  const snapshot = report.latest;
  if (!snapshot?.reportDate) return false;
  const capturedAt = new Date(snapshot.capturedAt);
  if (Number.isNaN(capturedAt.getTime())) return false;
  const capturedDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(
    capturedAt,
  );
  return Date.parse(capturedDate) - Date.parse(snapshot.reportDate) > 86_400_000;
}

function deviceLabel(device: TopvisorReport['device']): string {
  if (device === 'mobile') return 'смартфон';
  if (device === 'tablet') return 'планшет';
  return 'ПК';
}

function displayDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(`${value}T12:00:00`).toLocaleDateString('ru-RU');
}

function displayTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

function integerList(value: string): number[] {
  return stringList(value)
    .map(Number)
    .filter((item) => Number.isSafeInteger(item) && item > 0);
}

function stringList(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function message(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}
